/* eslint-disable max-lines -- one controller for voice identity on an
   editor row: apply + assign + enroll + chip + dialogs it hands back. */
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  useAssignVoiceIdentity,
  useClearVoiceIdentityOverride
} from '@utils/api/editors/voice-identifications';
import { Speaker } from '@utils/types/organization/speaker.ts';
import { VoiceIdentification } from '@utils/types/organization/voice-profile.ts';
import { voiceErrorKey } from '@utils/editors/voice-profile-errors.ts';
import { AssignPersonDialog } from './assign-person-dialog.tsx';
import { SaveVoiceSampleDialog } from './save-voice-sample-dialog.tsx';
import { VoiceSuggestionChip } from './voice-suggestion-chip.tsx';
import { useSpeakerNames } from './use-speaker-names.ts';
import { useVoiceAutoApply } from './use-voice-auto-apply.ts';
import { VoiceProfileFeature } from './use-voice-profile-feature.ts';
import { VoiceIdentityState } from './use-voice-identity.ts';

export interface VoiceSpeakerRowOptions {
  sessionId: string;
  documentId: string;
  /** Row position in the source audio, in milliseconds. */
  startMs: number;
  stopMs?: number;
  /** The original diarization label, when the caller already knows it.
   *
   *  A surface listing one row per detected voice — the Speakers panel —
   *  knows its label and must pass it. Re-deriving it from the row's time
   *  window is a guess at something already known, and that window spans
   *  nearly the whole recording in a conversation, so every decision landed
   *  on whichever voice talks most and overwrote the one before it (#2371).
   *  Editor rows that only know their position leave this out. */
  knownSpeakerLabel?: string | null;
  /** The display name currently on the row. */
  currentName: string;
  feature: VoiceProfileFeature;
  identity: VoiceIdentityState;
  /** Voice-wide rename: every paragraph of this voice gets the new name.
   *  Used by explicit user actions (directory list pick, "This voice
   *  everywhere", suggestion-chip accept).
   *
   *  The original diarization label travels with the name so the editor can
   *  rename that detected voice's rows. Renaming by display name instead
   *  reaches every row that happens to share this row's name, which is the
   *  other speaker's rows once two voices carry the same name — the
   *  "assigning the second speaker deletes the first" report (#2371). */
  onApplyName?: (name: string, speakerLabel: string) => void;
  /** Single-row rename: only this paragraph. Used by auto-apply so a
   *  mis-diarized file (sibling paragraphs sharing a label) is not renamed
   *  wholesale behind the user's back. */
  onApplyNameToSegment?: (name: string) => void;
  /** Default scope for dialog picks + suggestion accepts.
   *  - `'segment'` (default) — per-paragraph display only, no backend write.
   *    What the editor's inline picker wants so earlier per-paragraph picks
   *    aren't clobbered on mis-diarized files.
   *  - `'voice'` — voice-wide: commits backend identity AND propagates the
   *    name through `onApplyName`. What the Speakers panel wants because
   *    the panel exists to manage identity across the recording. */
  defaultAssignScope?: 'voice' | 'segment';
  /** Called the first time the user opens a voice control. The editor uses it
   *  to start fetching identity data it did not fetch up front. */
  onActivate?: () => void;
}

export interface VoiceSpeakerRow {
  /** The original diarization label for this row, if it resolves. */
  speakerLabel: string | null;
  identification: VoiceIdentification | undefined;
  /** True when any voice action is available on this row. */
  hasActions: boolean;
  /** Whether to offer the action — a question of role and surface only.
   *  Deliberately independent of whether the label has resolved yet: the
   *  timeline may not have been fetched, and a document may have no
   *  diarization at all. Both are explained inside the dialog. */
  canAssign: boolean;
  canEnroll: boolean;
  /** Set when this document can never support voice actions, so the editor
   *  can say why instead of silently offering nothing. */
  unavailableReason: 'no-diarization' | null;
  /** Record identity + apply name in one call. `scope: 'voice'` (default)
   *  renames every paragraph of this voice; `'segment'` touches only this
   *  one. Returns `false` on failure; nothing is re-thrown. */
  applyPerson: (
    speakerId: number | null,
    name?: string,
    options?: {
      silent?: boolean;
      offerEnrollment?: boolean;
      scope?: 'voice' | 'segment';
    }
  ) => Promise<boolean>;
  openAssign: () => void;
  openEnroll: () => void;
  /** Suggestion/decision chip to render beside the speaker label. */
  chip: ReactNode;
  /** Dialogs to render once per row. */
  dialogs: ReactNode;
}

/** Wire one editor row to voice identity: label, suggestion, dialogs.
 *
 * Both editors ask the same thing of this layer, so the transcript speaker
 * menu and the caption speakers panel share it rather than each growing their
 * own copy of the mutations and dialog state. */
export const useVoiceSpeakerRow = ({
  sessionId,
  documentId,
  startMs,
  stopMs,
  knownSpeakerLabel,
  currentName,
  feature,
  identity,
  onApplyName,
  onApplyNameToSegment,
  defaultAssignScope = 'segment',
  onActivate
}: VoiceSpeakerRowOptions): VoiceSpeakerRow => {
  const { t } = useTranslation();
  const [assignOpen, setAssignOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollAfterAssign, setEnrollAfterAssign] = useState(false);
  const [enrollPerson, setEnrollPerson] = useState<Speaker | null>(null);

  const assign = useAssignVoiceIdentity(sessionId, documentId);
  const clearOverride = useClearVoiceIdentityOverride(sessionId, documentId);

  const speakerLabel = useMemo(
    () => knownSpeakerLabel ?? identity.labelAt(startMs, stopMs),
    [knownSpeakerLabel, identity, startMs, stopMs]
  );

  const identification = speakerLabel
    ? identity.byLabel.get(speakerLabel)
    : undefined;

  // Only the ids this row actually references need resolving to names.
  const referencedIds = useMemo(() => {
    const ids: number[] = [];
    if (identification?.suggested_speaker_id != null)
      ids.push(identification.suggested_speaker_id);
    if (identification?.override_speaker_id != null)
      ids.push(identification.override_speaker_id);
    if (identification?.effective_speaker_id != null)
      ids.push(identification.effective_speaker_id);
    return ids;
  }, [identification]);

  const resolveSpeakerName = useSpeakerNames(
    referencedIds,
    feature.canAssign || feature.canReviewSuggestions
  );

  const labelGroup = useMemo(
    () => identity.labels.find(item => item.speaker_label === speakerLabel),
    [identity.labels, speakerLabel]
  );

  const handleAssign = async (speakerId: number | null, person?: Speaker) => {
    // Default scope is per-paragraph so earlier assignments aren't clobbered;
    // callers that manage identity at the voice level (the Speakers panel)
    // pass `defaultAssignScope: 'voice'` and get the full backend write.
    const ok = await applyPerson(speakerId, person?.name, {
      scope: defaultAssignScope
    });
    if (!ok) return;
    if (speakerId != null && person && enrollAfterAssign) {
      setEnrollPerson(person);
      setEnrollOpen(true);
    }
    setEnrollAfterAssign(false);
    setAssignOpen(false);
  };

  const handleClearOverride = async () => {
    if (!speakerLabel) return;
    try {
      await clearOverride.mutateAsync({ speakerLabel });
      toast.success(t('voice-decision-cleared'));
      setAssignOpen(false);
    } catch {
      // Shown inline in the dialog; see above.
    }
  };

  /** Accept the model's suggestion for THIS paragraph. Scope follows the
   *  caller's `defaultAssignScope`: editor rows default to segment so one
   *  click per paragraph; the Speakers panel uses voice-wide. */
  const handleUseSuggestion = async (speakerId: number, name: string) => {
    await applyPerson(speakerId, name, { scope: defaultAssignScope });
  };

  /** Identity + display update.
   *
   *  The backend stores ONE identity per voice label. On a well-diarized
   *  file each real speaker gets its own label, so one PUT per pick works.
   *  On a mis-diarized file (multiple real speakers folded into one label),
   *  the second PUT replaces the first — and in practice this is the
   *  "deletes the one I assigned before" complaint.
   *
   *  So picker picks and chip accepts are display-only by default
   *  (`scope: 'segment'`). The backend identity only gets committed when
   *  the user explicitly asks for voice-wide behaviour
   *  (`scope: 'voice'` — "This voice everywhere") or when enrollment
   *  requires it (chain-to-enroll path, which sets identity directly in
   *  `handleAssign`). Auto-apply is also segment-scope: it never clobbers
   *  anyone else's assignment behind the user's back. */
  const applyPerson = async (
    speakerId: number | null,
    name?: string,
    options?: {
      silent?: boolean;
      offerEnrollment?: boolean;
      scope?: 'voice' | 'segment';
    }
  ): Promise<boolean> => {
    if (!speakerLabel) return false;
    const silent = options?.silent ?? false;
    const scope = options?.scope ?? 'segment';
    const offerEnrollment =
      options?.offerEnrollment ??
      (!silent && speakerId != null && feature.canEnroll);

    // Segment scope: display only. No backend write, so earlier
    // assignments on the same voice label keep their identity.
    if (scope === 'segment') {
      if (speakerId !== null && name) {
        // Prefer the per-row callback (single paragraph), fall back to the
        // voice-wide one so surfaces that haven't wired the per-row path
        // still get a rename.
        if (onApplyNameToSegment) onApplyNameToSegment(name);
        else if (speakerLabel) onApplyName?.(name, speakerLabel);
      }
      if (silent) return true;
      const message = name
        ? t('voice-identity-linked', { name })
        : speakerId === null
          ? t('voice-marked-unknown')
          : t('voice-identity-saved');
      if (offerEnrollment && speakerId != null && name) {
        const person: Speaker = { id: speakerId, name };
        toast.success(
          tst => (
            <span className="flex items-center gap-3">
              <span>{message}</span>
              <button
                type="button"
                onClick={() => {
                  toast.dismiss(tst.id);
                  setEnrollPerson(person);
                  setEnrollOpen(true);
                }}
                className="cursor-pointer font-semibold underline"
              >
                {t('voice-remember-this-voice')}
              </button>
            </span>
          ),
          { duration: 10000 }
        );
      } else {
        toast.success(message);
      }
      return true;
    }

    try {
      await assign.mutateAsync({ speakerLabel, speakerId });
    } catch (error) {
      if (!silent) toast.error(t(voiceErrorKey(error)));
      return false;
    }
    if (speakerId !== null && name && onApplyName && speakerLabel)
      onApplyName(name, speakerLabel);
    if (silent) return true;
    const message =
      speakerId === null
        ? t('voice-marked-unknown')
        : name
          ? t('voice-identity-linked', { name })
          : t('voice-identity-saved');
    if (offerEnrollment && speakerId != null && name) {
      const person: Speaker = { id: speakerId, name };
      toast.success(
        tst => (
          <span className="flex items-center gap-3">
            <span>{message}</span>
            <button
              type="button"
              onClick={() => {
                toast.dismiss(tst.id);
                setEnrollPerson(person);
                setEnrollOpen(true);
              }}
              className="cursor-pointer font-semibold underline"
            >
              {t('voice-remember-this-voice')}
            </button>
          </span>
        ),
        { duration: 10000 }
      );
    } else {
      toast.success(message);
    }
    return true;
  };

  const openAssign = () => {
    onActivate?.();
    setEnrollAfterAssign(false);
    setAssignOpen(true);
  };

  const openEnroll = () => {
    onActivate?.();
    const personId = identification?.effective_speaker_id ?? null;
    const personName =
      personId != null ? resolveSpeakerName(personId) : undefined;
    // Voice-wide surfaces (the Speakers panel) go straight to enrolment with
    // whoever this voice is linked to — the panel is the place to manage
    // that link, so it's safe to assume. Segment-scope surfaces (the editor
    // row's "Save voice sample") always run the picker first because the
    // user may be enrolling someone different from the voice-wide link.
    if (defaultAssignScope === 'voice' && personId != null && personName) {
      setEnrollPerson({ id: personId, name: personName });
      setEnrollOpen(true);
      return;
    }
    setEnrollAfterAssign(true);
    setAssignOpen(true);
  };

  // Role and surface decide whether to offer the action. The label is resolved
  // when the dialog opens — it may need a fetch, and some documents have no
  // diarization at all — and the dialog explains either case.
  const canAssign = feature.canAssign;
  const canEnroll = feature.canEnroll && feature.sourceAvailable;

  const unavailableReason = identity.noDiarization ? 'no-diarization' : null;

  useVoiceAutoApply({
    documentId,
    speakerLabel,
    startMs,
    identification,
    currentName,
    canReviewSuggestions: feature.canReviewSuggestions,
    canAssign,
    scope: defaultAssignScope,
    resolveSpeakerName,
    applyPerson
  });

  const chip =
    identification &&
    (identification.override_set || feature.canReviewSuggestions) &&
    speakerLabel ? (
      <VoiceSuggestionChip
        identification={identification}
        resolveSpeakerName={resolveSpeakerName}
        currentName={currentName}
        onUseSuggestion={(id, name) => void handleUseSuggestion(id, name)}
        onChoosePerson={openAssign}
        disabled={!canAssign || assign.isLoading}
      />
    ) : null;

  const dialogs = (
    <>
      {assignOpen && (
        <AssignPersonDialog
          isOpen
          onClose={() => {
            setAssignOpen(false);
            setEnrollAfterAssign(false);
          }}
          speakerLabel={speakerLabel}
          // The timeline may still be in flight, or the document may have no
          // diarization — the dialog says so instead of showing an empty list.
          isResolvingLabel={identity.isLoading}
          unavailableReason={unavailableReason}
          identification={identification}
          resolveSpeakerName={resolveSpeakerName}
          onAssign={handleAssign}
          onClearOverride={
            identification?.override_set ? handleClearOverride : undefined
          }
          isSaving={assign.isLoading || clearOverride.isLoading}
          error={assign.error ?? clearOverride.error}
        />
      )}

      {enrollOpen && speakerLabel && enrollPerson && (
        <SaveVoiceSampleDialog
          isOpen
          onClose={() => setEnrollOpen(false)}
          sessionId={sessionId}
          documentId={documentId}
          speakerLabel={speakerLabel}
          label={labelGroup}
          person={enrollPerson}
          recognitionAvailable={feature.canReviewSuggestions}
          minSampleSeconds={feature.minSampleSeconds}
          maxSampleSeconds={feature.maxSampleSeconds}
          maxSamplesPerSpeaker={feature.maxSamplesPerSpeaker}
        />
      )}
    </>
  );

  return {
    speakerLabel,
    identification,
    hasActions: canAssign || canEnroll,
    canAssign,
    canEnroll,
    unavailableReason,
    applyPerson,
    openAssign,
    openEnroll,
    chip,
    dialogs
  };
};
